/**
 * access_log.repository.ts — 접속 기록(access_log) 본인 쓰기·본인 조회.
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md §4-2 access_log · §4-4 보관 5년 · §5-4 rules.
 *  - 문서 ID 자동. 비즈니스 키 없음.
 *  - 클라이언트는 본인 기기에서 일어난 사건(ACCESS_LOG_SELF_TYPES)만 자기 uid 로 만든다.
 *    rules: uid == 나, type ∈ accessLogSelfTypes(), 키 화이트리스트, login_id·name 문자열, by_uid/by_name null,
 *    created_at == request.time(serverTimestamp). expire_at 은 함수가 채움(+5년, 보관 ACCESS_LOG_RETENTION_YEARS).
 *  - 관리자·함수가 일으키는 사건(강제 로그아웃·계정 정지 등)은 함수만 쓴다.
 *  - 전체 조회(통합관리 B-5)는 C 묶음의 별도 파일에서 — 여기는 본인 것만.
 *
 * 기록은 안내용이라(§10-3) 쓰기 실패가 로그인·로그아웃을 막으면 안 된다 → 실패는 콘솔 경고만.
 */
import {
  addDoc,
  collection,
  limit as qLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  where,
  type DocumentData,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  isAccessLogType,
  type AccessLogEntry,
  type AccessLogSelfType,
  type DeviceKind,
} from "@/domain/session";
import { isTenantKey, type TenantKey } from "@/domain/space";

function toDate(v: unknown): Date | null {
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  return null;
}

/** 알 수 없는 종류는 null(화면에서 뺀다) — 조용히 다른 종류로 바꿔 보이지 않게 */
export function accessLogFromFirestore(id: string, d: DocumentData): AccessLogEntry | null {
  if (!isAccessLogType(d.type)) {
    console.warn("[access_log] 알 수 없는 type", id, d.type);
    return null;
  }
  const kind: DeviceKind | null = d.kind === "pc" || d.kind === "mo" ? d.kind : null;
  return {
    id,
    uid: String(d.uid ?? ""),
    login_id: String(d.login_id ?? ""),
    name: String(d.name ?? ""),
    type: d.type,
    device_id: typeof d.device_id === "string" ? d.device_id : null,
    device_name: typeof d.device_name === "string" ? d.device_name : null,
    browser: typeof d.browser === "string" ? d.browser : null,
    os: typeof d.os === "string" ? d.os : null,
    kind,
    shared_pc: d.shared_pc === true,
    by_uid: typeof d.by_uid === "string" ? d.by_uid : null,
    by_name: typeof d.by_name === "string" ? d.by_name : null,
    tenants: Array.isArray(d.tenants) ? (d.tenants as unknown[]).filter(isTenantKey) : [],
    created_at: toDate(d.created_at),
    expire_at: toDate(d.expire_at),
  };
}

export interface SelfAccessLogInput {
  uid: string;
  login_id: string;
  name: string;
  type: AccessLogSelfType;
  device: { id: string; name: string; browser: string; os: string; kind: DeviceKind } | null;
  shared_pc: boolean;
  tenants: TenantKey[];
}

/** 본인 접속 기록 1건 남기기. 실패해도 throw 하지 않는다 */
export async function writeSelfAccessLog(input: SelfAccessLogInput): Promise<void> {
  try {
    await addDoc(collection(db, "access_log"), {
      uid: input.uid,
      login_id: input.login_id,
      name: input.name,
      type: input.type,
      device_id: input.device?.id ?? null,
      device_name: input.device?.name ?? null,
      browser: input.device?.browser ?? null,
      os: input.device?.os ?? null,
      kind: input.device?.kind ?? null,
      shared_pc: input.shared_pc,
      by_uid: null,
      by_name: null,
      tenants: input.tenants,
      created_at: serverTimestamp(),
      // expire_at 은 넣지 않는다 — 함수 onAccessLogCreate 가 created_at + 5년으로 채운다(A 계약)
    });
  } catch (e) {
    console.warn("[access_log] 기록 실패(무시)", input.type, e);
  }
}

/**
 * 내 접속 기록 최근 n건 구독(D-3). 복합 인덱스 (uid ASC, created_at DESC) 필요 — A 묶음 firestore.indexes.json.
 * 서버 타임스탬프 확정 전 항목도 시각이 비지 않게 serverTimestamps:"estimate" 로 읽는다.
 */
export function watchMyAccessLog(
  uid: string,
  n: number,
  cb: (rows: AccessLogEntry[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const q = query(collection(db, "access_log"), where("uid", "==", uid), orderBy("created_at", "desc"), qLimit(n));
  return onSnapshot(
    q,
    { includeMetadataChanges: false },
    (snap) => {
      const rows: AccessLogEntry[] = [];
      snap.docs.forEach((s) => {
        const r = accessLogFromFirestore(s.id, s.data({ serverTimestamps: "estimate" }));
        if (r) rows.push(r);
      });
      cb(rows);
    },
    (e) => onError?.(e),
  );
}
